import { Injectable } from '@angular/core';
import BigNumber from 'bignumber.js';
import { BehaviorSubject, combineLatest, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import config from '../../data/config.json';
import stakingAbi from '../../data/staking-abi.json';
import { PlanModel } from '../../models/plan-model';
import { StakingSettingsModel } from '../../models/staking-settings.model';
import { BlocknativeService } from '../blocknative.service';
import { ButtonActiveService } from '../button-active.service';
import { TokenService } from './token.service';

@Injectable({
  providedIn: 'root',
})
export class StakingService {
  private contract: any;

  private stakingData = new BehaviorSubject({
    stakedValue: '0',
    stakes: [] as any[],
    rewards: '0',
  });
  stakingData$ = this.stakingData.asObservable();

  conversionRate = 0;

  constructor(
    private bncService: BlocknativeService,
    private buttonActiveSvc: ButtonActiveService,
    private tokenService: TokenService
  ) {
    this.getConversionRate();
    this._setContract();
  }

  async stakeToken(value: BigNumber, planId: number) {
    this.buttonActiveSvc.staking.next(true);
    const allowance = await this.tokenService.getAllowance(
      this.contract._address
    );
    console.log(this.contract._address);
    if (new BigNumber(allowance).isLessThan(value)) {
      await this.tokenService.approve(this.contract._address, value.toFixed());
    }
    await this.contract.methods
      .stake(value.toFixed(), planId)
      .send({ from: this.bncService.address, gasPrice: 5_000_000_000 });

    this.getStakingData();
    this.tokenService.refresh();
    this.buttonActiveSvc.staking.next(false);
  }

  async unstakeToken(tokenIds: string[]) {
    this.buttonActiveSvc.unstaking.next(true);

    await this.contract.methods
      .unstake(tokenIds)
      .send({ from: this.bncService.address, gasPrice: 5_000_000_000 });

    this.getStakingData();
    this.tokenService.refresh();
    this.buttonActiveSvc.unstaking.next(false);
  }

  async claimReward(tokenIds: number[]) {
    this.buttonActiveSvc.claim.next(true);
    await this.contract.methods
      .claimRewards(tokenIds)
      .send({ from: this.bncService.address, gasPrice: 5_000_000_000 });
    this.buttonActiveSvc.claim.next(false);
    this.tokenService.refresh();
    this.getStakingData();
  }

  private async getStakingData() {
    const stakesResponse = await this.contract.methods
      .getStakesForUser(this.bncService.address)
      .call();

    const rewards = await this.contract.methods
      .getRewards(stakesResponse.map((item: any) => item.tokenId))
      .call({ from: this.bncService.address });

    const stakingSettings = this.getStakingSettings(this.bncService.networkId!);

    const stakes: any[] = [];
    let staked = new BigNumber(0);
    for (const stake of stakesResponse) {
      staked = staked.plus(new BigNumber(stake.amount));
      const totalRewardValue = this.getTotalRewardsForStake(
        stake,
        stakingSettings
      );
      stakes.push({
        tokenId: stake.tokenId,
        finished: stake.unstakedAt !== '0',
        amount: stake.amount,
        claimedRewards: stake.claimed,
        totalRewards: totalRewardValue,
        availableRewards: new BigNumber(totalRewardValue)
          .minus(new BigNumber(stake.claimed))
          .toString(),
        stakeDate: +stake.stakedAt,
        unlockDate:
          +stake.stakedAt + stakingSettings.plans[stake.planId].lockDuration,
        endRewardDate: stakingSettings.normalRewardEndTime,
        currentAPY:
          stakingSettings.doubleRewardEndTime < Date.now() / 1000
            ? 2 * stakingSettings.plans[stake.planId].apy
            : stakingSettings.plans[stake.planId].apy,
        unlockTimer$: timer(0, 1000).pipe(
          map(() =>
            this.calculateCountdown(
              +stake.stakedAt + stakingSettings.plans[stake.planId].lockDuration
            )
          )
        ),
        endRewardTimer$: timer(0, 1000).pipe(
          map(() =>
            this.calculateCountdown(stakingSettings.normalRewardEndTime)
          )
        ),
      });
    }

    this.stakingData.next({
      stakedValue: staked.toString(),
      stakes: stakes,
      rewards: rewards.toString(),
    });
  }

  private _setContract() {
    combineLatest([
      this.bncService.address$,
      this.bncService.networkId$,
    ]).subscribe(([address, networkId]) => {
      if (!networkId || !address) return;

      const network = config.networks.find(
        (network) => network.networkId == networkId
      );

      if (!network) return;

      this.contract = new this.bncService.web3.eth.Contract(
        stakingAbi,
        network.stakingContractAddress
      );

      this.getStakingData();
    });
  }

  private getTotalRewardsForStake(
    stakeObject: any,
    stakingSettings: StakingSettingsModel
  ): string {
    const apy = stakingSettings.plans[stakeObject.planId].apy;

    const currentTime = Math.round(Date.now() / 1000);

    // set endDate & endDateDR on unstakedAt or now
    let endDate =
      stakeObject.unstakedAt > 0 ? stakeObject.unstakedAt : currentTime;

    let endDateDR = endDate;

    // if any of dates is biger than its limit, lower it to its limit
    if (endDate > stakingSettings.normalRewardEndTime) {
      endDate = stakingSettings.normalRewardEndTime;
    }
    if (endDateDR > stakingSettings.doubleRewardEndTime) {
      endDateDR = stakingSettings.doubleRewardEndTime;
    }

    const doubleRewardsDays =
      endDateDR > stakeObject.stakedAt
        ? Math.floor((endDateDR - stakeObject.stakedAt) / (3600 * 24))
        : 0;
    const normalRewardsDays = Math.floor(
      (endDate - stakeObject.stakedAt) / (3600 * 24)
    );

    const doubleRewards = new BigNumber(stakeObject.amount)
      .multipliedBy(apy)
      .multipliedBy(doubleRewardsDays)
      .div(36500);
    const normalRewards = new BigNumber(stakeObject.amount)
      .multipliedBy(apy)
      .multipliedBy(normalRewardsDays)
      .div(36500);

    return doubleRewards.plus(normalRewards).toString();
  }

  private calculateCountdown(datetime: number): string {
    if (!datetime) {
      return '';
    }

    const seconds = Math.round(datetime - Date.now() / 1000);

    if (seconds <= 0) {
      if (seconds === 0) {
        this.getStakingData().then();
      }
      return '';
    } else {
      const days = Math.floor(seconds / 86400);
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor(seconds / 60);

      const dayString = days ? `${days}d ` : '';

      return `${dayString}${(hours % 24).toString().padStart(2, '0')}:${(
        minutes % 60
      )
        .toString()
        .padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
    }
  }

  private getConversionRate() {
    fetch(
      'https://api.coingecko.com/api/v3/simple/price?ids=ruby-play-network&vs_currencies=usd'
    )
      .then((res) => res.json())
      .then((res) => (this.conversionRate = res['ruby-play-network'].usd));
  }

  private getStakingSettings(networkId: number) {
    switch (networkId) {
      case 1:
        return new StakingSettingsModel(1687950000, 1636812000, 1639404000, [
          new PlanModel(0, 8),
          new PlanModel(30 * 3600 * 24, 20),
          new PlanModel(90 * 3600 * 24, 30),
          new PlanModel(180 * 3600 * 24, 40),
        ]);
      case 56:
        return new StakingSettingsModel(1687950000, 1647342000, 1649934000, [
          new PlanModel(0, 5),
          new PlanModel(90 * 3600 * 24, 10),
          new PlanModel(180 * 3600 * 24, 15),
          new PlanModel(360 * 3600 * 24, 20),
        ]);
      default:
        return new StakingSettingsModel(1687950000, 1636812000, 1639404000, [
          new PlanModel(0, 8),
          new PlanModel(30 * 3600 * 24, 20),
          new PlanModel(90 * 3600 * 24, 30),
          new PlanModel(180 * 3600 * 24, 40),
        ]);
    }
  }
}
